import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePane } from '@/components/data';
import {
  balanceOf, modeLabel, money, ON_ACCOUNT, PAYMENT_MODES,
  paymentNote, REFERENCE_REQUIRED_MODES,
} from '@/lib/payments';

/**
 * Taking a payment, docked beside the list the clerk was reading.
 *
 * It opens saying what the customer owes, because that is the question asked at
 * the counter before the amount is, and it will fill the amount in for you —
 * a balance typed by hand is a balance mistyped. What it will not do is let the
 * money land somewhere it cannot go: the same rules the API enforces are
 * checked here so the answer arrives before the request does.
 */
export function RecordPaymentPane({ payment }) {
  const {
    customer, isOpen, orders, outstanding, advance, loading,
    form, setForm, error, submitting, close, submit,
  } = payment;

  const selected = orders.find((o) => o.id === form.order_id);
  const known = outstanding !== null && outstanding !== undefined;
  const target = selected ? balanceOf(selected) : (known ? outstanding : 0);
  const note = paymentNote(form, { outstanding });
  const referenceRequired = REFERENCE_REQUIRED_MODES.includes(form.mode);

  return (
    <SidePane
      open={isOpen}
      onClose={close}
      title="Record payment"
      subtitle={customer?.customer_name}
      width="24rem"
      data-testid="record-payment-pane"
      footer={(
        <>
          <Button variant="outline" size="sm" onClick={close}>Cancel</Button>
          <Button
            size="sm" onClick={submit} disabled={submitting || loading}
            data-testid="record-payment-btn"
            className="bg-brand-blue text-white hover:bg-brand-blue-dark"
          >
            {submitting ? 'Saving…' : 'Record payment'}
          </Button>
        </>
      )}
    >
      <div className="space-y-2.5 p-3">
        {(known || loading) && (
          <div
            className="flex items-baseline justify-between rounded border border-slate-200 bg-slate-50 px-2.5 py-2"
            data-testid="payment-outstanding"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Outstanding</span>
            <span className={`text-[15px] font-bold tabular-nums ${outstanding > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {loading ? '…' : money(outstanding)}
            </span>
          </div>
        )}
        {advance > 0 && (
          <p className="text-[11px] text-slate-500" data-testid="payment-advance">
            {money(advance)} already held as advance credit for this customer.
          </p>
        )}

        <label className="block">
          <span className="text-[11px] font-medium text-slate-600">Against order</span>
          <Select value={form.order_id} onValueChange={(v) => setForm({ order_id: v })}>
            <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" data-testid="payment-order-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ON_ACCOUNT} className="text-xs">On account (oldest orders first)</SelectItem>
              {orders.map((o) => (
                <SelectItem key={o.id} value={o.id} className="text-xs">
                  {o.order_id} — {money(balanceOf(o))} due
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="flex items-baseline justify-between">
              <span className="text-[11px] font-medium text-slate-600">Amount *</span>
              {target > 0 && (
                <button
                  type="button"
                  data-testid="payment-fill-balance"
                  onClick={() => setForm({ amount: String(target) })}
                  className="text-[10px] font-medium text-brand-blue hover:underline"
                >
                  Full {money(target)}
                </button>
              )}
            </span>
            <Input
              type="number" inputMode="decimal" min="0" step="0.01"
              data-testid="payment-amount-input"
              className="mt-0.5 h-10 text-base tabular-nums sm:h-8 sm:text-sm"
              value={form.amount}
              onChange={(e) => setForm({ amount: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Mode *</span>
            <Select value={form.mode} onValueChange={(v) => setForm({ mode: v })}>
              <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" data-testid="payment-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_MODES.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">{modeLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <label className="block">
          <span className="text-[11px] font-medium text-slate-600">
            Reference {referenceRequired ? '*' : ''}
          </span>
          <Input
            className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"
            placeholder={form.mode === 'cheque' ? 'Cheque number' : 'UTR, transaction reference…'}
            data-testid="payment-reference-input"
            value={form.reference_number}
            onChange={(e) => setForm({ reference_number: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-medium text-slate-600">Remarks</span>
          <Textarea
            className="mt-0.5 resize-none text-sm" rows={2}
            data-testid="payment-remarks-input"
            value={form.remarks}
            onChange={(e) => setForm({ remarks: e.target.value })}
          />
        </label>

        {error && (
          <p className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-snug text-red-700"
            data-testid="payment-error" role="alert">
            {error}
          </p>
        )}
        {!error && note && (
          <p className="text-[11px] leading-snug text-slate-500" data-testid="payment-note">{note}</p>
        )}
      </div>
    </SidePane>
  );
}

export default RecordPaymentPane;
